import React, { useMemo, useEffect, useRef } from 'react';
import { Code, Terminal, Cpu, Database, Globe, Server, Laptop, Keyboard, Bug, Cloud, Zap, Lock } from 'lucide-react';

const BackgroundAnimation = React.memo(({ color }) => {
  const containerRef = useRef(null);
  const icons = [
    Code, Terminal, Cpu, Database, Globe, Server, 
    Laptop, Keyboard, Bug, Cloud, Zap, Lock
  ];

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!containerRef.current) return;
      const x = (e.clientX / window.innerWidth - 0.5);
      const y = (e.clientY / window.innerHeight - 0.5);
      containerRef.current.style.setProperty('--mouse-x', x);
      containerRef.current.style.setProperty('--mouse-y', y);
    };

    const handleDeviceOrientation = (e) => {
      if (!containerRef.current) return;
      // gamma: Links/Rechts Neigung (typischerweise -90 bis 90)
      // beta: Vor/Zurück Neigung (typischerweise -180 bis 180)
      let gamma = e.gamma || 0;
      let beta = e.beta || 0;

      // Neigungswinkel auf +/- 45 Grad begrenzen
      const x = Math.max(-45, Math.min(45, gamma)) / 90;
      // Beta auf einen typischen "Handy-in-der-Hand"-Winkel (45 Grad) zentrieren
      const y = Math.max(-45, Math.min(45, beta - 45)) / 90;

      containerRef.current.style.setProperty('--mouse-x', x);
      containerRef.current.style.setProperty('--mouse-y', y);
    };

    window.addEventListener('mousemove', handleMouseMove);
    
    // Nur hinzufügen, wenn der Browser es unterstützt (Gyroscope/Accelerometer)
    if (window.DeviceOrientationEvent) {
      window.addEventListener('deviceorientation', handleDeviceOrientation);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (window.DeviceOrientationEvent) {
        window.removeEventListener('deviceorientation', handleDeviceOrientation);
      }
    };
  }, []);

  const iconElements = useMemo(() => {
    return icons.map((Icon, i) => {
      // Grid 3x4 für 12 Icons, um Überlappungen zu vermeiden
      const cols = 3;
      const row = Math.floor(i / cols);
      const col = i % cols;
      
      // Zufällige Position innerhalb der Grid-Zelle mit etwas Abstand
      const top = (row * 25) + Math.random() * 15; 
      const left = (col * 33) + Math.random() * 20;

      // Parallax-Tiefe (0.1 bis 1.0)
      const depth = 0.2 + Math.random() * 0.8;
      const size = 20 + depth * 40; // Größere Icons sind "näher"
      const parallaxFactor = depth * 40; // Nähere Icons bewegen sich mehr

      return (
        <div key={i} style={{
          position: 'absolute',
          top: `${top}%`,
          left: `${left}%`,
          transform: `translate(calc(var(--mouse-x, 0) * ${parallaxFactor}px), calc(var(--mouse-y, 0) * ${parallaxFactor}px))`,
          transition: 'transform 0.1s linear',
          zIndex: 0
        }}>
          <div style={{
            opacity: 0.1 + depth * 0.15,
            color: 'inherit',
            animation: `float ${10 + Math.random() * 10}s ease-in-out infinite alternate`
          }}>
            <Icon size={size} />
          </div>
        </div>
      );
    });
  }, []);

  return (
    <div ref={containerRef} className="background-animation" style={{
      position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', overflow: 'hidden', pointerEvents: 'none', zIndex: 0,
      color: color || '#1cb0f6',
      transition: 'color 0.5s ease'
    }}>
      {iconElements}
      <style>{`
        @keyframes float {
          from { transform: translateY(0) rotate(0deg); }
          to { transform: translateY(-20px) rotate(15deg); }
        }
      `}</style>
    </div>
  );
});

export default BackgroundAnimation;
